import { useEffect, useState } from "react";
import { Save, Loader2, X, ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { errMsg, mediaUrl } from "@/lib/api";
import { useApi, PageHead, UploadButton } from "@/admin/AdminApp";

const GROUPS = [
  ["The couple", [["couple_first", "First name line (e.g. Yash Jain)"], ["couple_second", "Second name line (e.g. Suhani Kataria)"], ["short_names", "Short names (header & emails)"], ["together_line", "Line above names"]]],
  ["The wedding", [["date_text", "Wedding dates (as shown)"], ["location", "Location"], ["countdown_caption", "Countdown caption"]]],
  ["Page text", [["rsvp_heading", "RSVP question"], ["reg_heading", "Registration heading"], ["reg_intro", "Registration intro", true], ["stay_heading", "Stay heading"], ["stay_intro", "Stay intro", true], ["footer_text", "Footer text"]]],
];

const toLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(new Date(iso).getTime() + 330 * 60000);
  return d.toISOString().slice(0, 16);
};

export default function Content() {
  const api = useApi();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api("get", "/settings").then(setS).catch((e) => toast.error(errMsg(e))); }, [api]);
  if (!s) return <Loader2 className="mx-auto animate-spin text-[var(--gold)]" />;

  const save = async () => {
    setBusy(true);
    try { setS(await api("put", "/settings", s)); toast.success("Content saved"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const moveImg = (i, d) => { const a = [...s.carousel]; [a[i], a[i + d]] = [a[i + d], a[i]]; setS({ ...s, carousel: a }); };

  return (
    <div className="mx-auto max-w-4xl pb-20" data-testid="content-page">
      <PageHead title="Content" text="Couple names, dates, countdown, home photos and page text shown to guests.">
        <button className="btn-ink !py-2 text-sm" onClick={save} disabled={busy} data-testid="save-content-btn">{busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save</button>
      </PageHead>
      {GROUPS.map(([title, items]) => (
        <div key={title} className="mb-6 rounded-2xl border border-[var(--line)] bg-white p-6">
          <h2 className="font-serif-d mb-4 text-2xl">{title}</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {items.map(([k, l, area]) => (
              <label key={k} className={`block ${area ? "sm:col-span-2" : ""}`}><span className="label-lux">{l}</span>
                {area ? <textarea className="input-lux min-h-[80px]" value={s[k] || ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} data-testid={`content-${k}-input`} />
                  : <input className="input-lux" value={s[k] || ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} data-testid={`content-${k}-input`} />}
              </label>
            ))}
            {title === "The wedding" && (
              <label className="block"><span className="label-lux">Countdown target (India time)</span>
                <input type="datetime-local" className="input-lux" value={toLocal(s.countdown_target)} onChange={(e) => setS({ ...s, countdown_target: `${e.target.value}:00+05:30` })} data-testid="content-countdown-input" />
              </label>
            )}
          </div>
        </div>
      ))}
      <div className="rounded-2xl border border-[var(--line)] bg-white p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-serif-d text-2xl">Home photos</h2>
          <UploadButton multiple label="Add photos" testId="carousel-upload" onUploaded={(r) => setS((cur) => ({ ...cur, carousel: [...cur.carousel, r.url] }))} />
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {s.carousel.map((u, i) => (
            <div key={`${u}-${i}`} className="group relative overflow-hidden rounded-xl" data-testid={`carousel-item-${i}`}>
              <img src={mediaUrl(u)} alt="" className="aspect-[4/5] w-full object-cover" />
              <div className="absolute inset-x-0 bottom-0 flex justify-between bg-gradient-to-t from-black/60 p-2 text-white">
                <button disabled={i === 0} onClick={() => moveImg(i, -1)} className="disabled:opacity-30" data-testid={`carousel-left-${i}`}><ArrowLeft size={16} /></button>
                <button onClick={() => setS({ ...s, carousel: s.carousel.filter((_, j) => j !== i) })} data-testid={`carousel-remove-${i}`}><X size={16} /></button>
                <button disabled={i === s.carousel.length - 1} onClick={() => moveImg(i, 1)} className="disabled:opacity-30" data-testid={`carousel-right-${i}`}><ArrowRight size={16} /></button>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs text-slate-500">Remember to press Save after changing photos.</p>
      </div>
    </div>
  );
}
